import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import api from "../../utils/api";

const BillingSummaryExport = ({ data, loading }) => {
  const [filters, setFilters] = useState({
    workOrderId: "",
    itemGroup: "",
    fromDate: "",
    toDate: "",
    association: "",
  });

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // details keyed by work order id
  const [workOrderDetails, setWorkOrderDetails] = useState({});
  // loading status keyed by work order id
  const [loadingDetails, setLoadingDetails] = useState({});

  // ✅ Fetch Work Order Types and Subtypes
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const [typeRes, subtypeRes] = await Promise.all([
          api.get("/workorder-types"),
          api.get("/workorder-subtypes"),
        ]);
        setTypes(typeRes.data);
        setSubtypes(subtypeRes.data);
      } catch (err) {
        console.error("Failed to fetch types/subtypes", err);
      }
    };
    fetchTypes();
  }, []);

  const getTypeName = (id) => types.find((t) => t.id === id)?.type || "—";
  const getSubtypeName = (id) =>
    subtypes.find((s) => s.id === id)?.subtype_name || "—";

  // ✅ Apply filters
  const filteredData = useMemo(() => {
    return (data || []).filter((item) => {
      const matchesWO =
        filters.workOrderId === "" ||
        `${item.work_order_id}`.includes(filters.workOrderId);

      const matchesGroup =
        filters.itemGroup === "" ||
        (item.item_group || "")
          .toLowerCase()
          .includes(filters.itemGroup.toLowerCase());

      const matchesAssoc =
        filters.association === "" ||
        (item.association_name || "")
          .toLowerCase()
          .includes(filters.association.toLowerCase());

      const sentAt = item.sent_at ? dayjs(item.sent_at) : null;

      const matchesFrom =
        !filters.fromDate ||
        (sentAt && sentAt.isAfter(dayjs(filters.fromDate).subtract(1, "day")));

      const matchesTo =
        !filters.toDate ||
        (sentAt && sentAt.isBefore(dayjs(filters.toDate).add(1, "day")));

      return (
        matchesWO && matchesGroup && matchesAssoc && matchesFrom && matchesTo
      );
    });
  }, [data, filters]);

  const groupedByWO = useMemo(
    () => [...new Set(filteredData.map((d) => d.work_order_id))],
    [filteredData]
  );

  const paginatedWO = useMemo(
    () =>
      groupedByWO.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [groupedByWO, page, rowsPerPage]
  );

  // ✅ Fetch Work Order Details
  // This fetches details for ALL unique WOs in filteredData (current behavior).
  // If you want it faster, you can switch this to paginatedWO only.
  useEffect(() => {
    const fetchWorkOrderDetails = async () => {
      const uniqueIds = [...new Set(filteredData.map((d) => d.work_order_id))];
      if (uniqueIds.length === 0) return;

      // Only fetch ids we don't already have and aren't currently loading
      const idsToFetch = uniqueIds.filter(
        (id) => !workOrderDetails[id] && !loadingDetails[id]
      );
      if (idsToFetch.length === 0) return;

      // mark as loading
      setLoadingDetails((prev) => {
        const next = { ...prev };
        idsToFetch.forEach((id) => (next[id] = true));
        return next;
      });

      const detailsMap = {};

      await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await api.get(`/getWorkOrderReportbilling/${id}`);
            detailsMap[id] = res.data;
          } catch (err) {
            console.error(`Failed to fetch details for Work Order ${id}`, err);
          } finally {
            setLoadingDetails((prev) => ({ ...prev, [id]: false }));
          }
        })
      );

      // merge in newly fetched details
      setWorkOrderDetails((prev) => ({ ...prev, ...detailsMap }));
    };

    fetchWorkOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  // ✅ Export to CSV (includes Member Address)
  const handleExportCSV = () => {
    const headers = [
      "Work Order ID",
      "Association",
      "Member Address",
      "Item Group",
      "Description",
      "Details",
      "Price",
      "Sent At",
      "Transferred",
      "Resolved At",
      "Type",
      "Subtype",
    ];

    const rows = filteredData.map((item) => {
      const wo = workOrderDetails[item.work_order_id];
      const detailsText = wo?.details || item.details || "—";
      const memberAddress = wo?.full_address || "—";

      return [
        item.work_order_id,
        item.association_name || "—",
        memberAddress,
        item.item_group || "—",
        item.export_description || "—",
        (detailsText || "—").replace(/\r?\n/g, " ").trim() || "—",
        item.price ? parseFloat(item.price).toFixed(2) : "0.00",
        item.sent_at ? dayjs(item.sent_at).format("YYYY-MM-DD") : "—",
        item.exported ? "Yes" : "No",
        item.work_order_resolved_at
          ? dayjs(item.work_order_resolved_at).format("YYYY-MM-DD")
          : "—",
        getTypeName(item.workorder_type_id),
        getSubtypeName(item.workorder_subtype_id),
      ];
    });

    const csvContent =
      [headers, ...rows]
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
        .join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    saveAs(blob, `billing_export_${dayjs().format("YYYYMMDD_HHmmss")}.csv`);
  };

  const handleResetFilters = () => {
    setFilters({
      workOrderId: "",
      itemGroup: "",
      fromDate: "",
      toDate: "",
      association: "",
    });
    setPage(0);
  };

  if (loading) return <Typography>Loading report...</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        💼 Billed Work Orders Report
      </Typography>

      {/* 🔍 FILTERS */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <TextField
          label="Work Order ID"
          size="small"
          value={filters.workOrderId}
          onChange={(e) =>
            setFilters({ ...filters, workOrderId: e.target.value })
          }
        />
        <TextField
          label="Item Group"
          size="small"
          value={filters.itemGroup}
          onChange={(e) =>
            setFilters({ ...filters, itemGroup: e.target.value })
          }
        />
        <TextField
          label="Association"
          size="small"
          value={filters.association}
          onChange={(e) =>
            setFilters({ ...filters, association: e.target.value })
          }
        />

        <TextField
          label="From Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.fromDate}
          onChange={(e) =>
            setFilters({ ...filters, fromDate: e.target.value })
          }
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={filters.toDate}
          onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
        />

        <Button
          variant="outlined"
          onClick={handleExportCSV}
          sx={{ alignSelf: "center" }}
        >
          Export CSV
        </Button>

        <Button
          variant="text"
          onClick={handleResetFilters}
          sx={{ alignSelf: "center" }}
        >
          Reset
        </Button>
      </Box>

      {/* 🧾 WORK ORDERS LIST */}
      {groupedByWO.length === 0 && (
        <Typography>No results found for selected filters.</Typography>
      )}

      {paginatedWO.map((woId) => {
        const group = filteredData.filter((d) => d.work_order_id === woId);
        const assocName = group[0]?.association_name || "—";
        const woTotal = group[0]?.work_order_total || 0;
        const resolved = group[0]?.work_order_resolved_at;
        const woType = group[0]?.workorder_type_id;
        const woSubType = group[0]?.workorder_subtype_id;

        const details = workOrderDetails[woId];
        const isDetailsLoading = !!loadingDetails[woId] && !details;

        const createdAt = details?.created_at
          ? dayjs(details.created_at).format("YYYY-MM-DD")
          : "—";

        const openedBy = details?.opened_by_name || "—";
        const priority = details?.priority || "—";
        const assignedTo = details?.assign_to || "—";
        const memberAddress = details?.full_address || "—";

        return (
          <Box
            key={woId}
            sx={{
              mb: 4,
              p: 3,
              borderRadius: 3,
              boxShadow: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Work Order #{woId}
              </Typography>
              {isDetailsLoading && <CircularProgress size={16} />}
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ color: "text.secondary", mb: 0.25 }}
            >
              {assocName}
            </Typography>

            {/* Address */}
            {isDetailsLoading ? (
              <Skeleton variant="text" width={320} height={20} />
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Address: {memberAddress}
              </Typography>
            )}

            {/* Meta line */}
            {isDetailsLoading ? (
              <Skeleton variant="text" width="90%" height={20} />
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Type: {getTypeName(woType)} | Subtype:{" "}
                {getSubtypeName(woSubType)} | Created: {createdAt} | Resolved:{" "}
                {resolved ? dayjs(resolved).format("YYYY-MM-DD") : "—"} | Priority:{" "}
                {priority} | Assigned To: {assignedTo} | Opened By: {openedBy}
              </Typography>
            )}

            {/* Details */}
            {isDetailsLoading ? (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  boxShadow: "inset 0 0 4px rgba(0,0,0,0.1)",
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Details
                </Typography>
                <Skeleton variant="text" width="95%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="85%" />
              </Box>
            ) : (
              details?.details && (
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    boxShadow: "inset 0 0 4px rgba(0,0,0,0.1)",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    Details
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-line", color: "text.secondary" }}
                  >
                    {details.details}
                  </Typography>
                </Box>
              )
            )}

            {/* 📋 TABLE */}
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#eee" }}>
                  <TableRow>
                    <TableCell>Item Group</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Sent At</TableCell>
                    <TableCell>Transferred to Jonas</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.item_group || "—"}</TableCell>
                      <TableCell>{item.export_description || "—"}</TableCell>
                      <TableCell>
                        $
                        {parseFloat(item.price || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        {item.sent_at
                          ? dayjs(item.sent_at).format("YYYY-MM-DD")
                          : "—"}
                      </TableCell>
                      <TableCell>{item.exported ? "Yes" : "No"}</TableCell>
                    </TableRow>
                  ))}

                  <TableRow sx={{ backgroundColor: "#f1f1f1" }}>
                    <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                      Work Order Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      $
                      {parseFloat(woTotal).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      <TablePagination
        component="div"
        count={groupedByWO.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Work Orders per page:"
      />
    </Box>
  );
};

export default BillingSummaryExport;
